// CustomCategoryTreeComponent.js
import { Formio } from "formiojs";
import Field from "formiojs/components/_classes/field/Field";

class CustomCategoryTreeComponent extends Field {
  static schema(...extend) {
    return Field.schema({
      type: "categoryTree",
      label: "Categories",
      key: "categories",
      multiple: false,
      maxItemCount: -1,
      data: { tree: [] },
      ...extend,
    });
  }

  static get builderInfo() {
    return {
      title: "Category Tree",
      icon: "sitemap",
      group: "basic",
      weight: 70,
      schema: CustomCategoryTreeComponent.schema(),
    };
  }

  get defaultSchema() {
    return CustomCategoryTreeComponent.schema();
  }

  constructor(component, options, data) {
    super(component, options, data);
    this.expandedNodes = new Set();
    this.selectedValues = new Set();
    this.allExpandableNodeIds = [];
  }

  get inputInfo() {
    const info = super.inputInfo;
    info.type = "input";
    info.changeEvent = "change";
    return info;
  }

  /**
   * Render date badge HTML with translated status label
   */
  renderDateBadge(node) {
    if (!node.status_label) return "";

    const statusClass = node.status ? `ct-badge--${node.status}` : "";
    return `<span class="ct-badge ${statusClass}">${node.status_label}</span>`;
  }

  /**
   * Collect all expandable node IDs (nodes with children)
   */
  collectExpandableNodeIds(nodes, ids = []) {
    if (!nodes || nodes.length === 0) return ids;

    nodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        ids.push(String(node.id));
        this.collectExpandableNodeIds(node.children, ids);
      }
    });

    return ids;
  }

  /**
   * Check if all expandable nodes are currently expanded
   */
  areAllNodesExpanded() {
    if (this.allExpandableNodeIds.length === 0) return false;
    return this.allExpandableNodeIds.every(id => this.expandedNodes.has(id));
  }

  /**
   * Toggle all nodes expanded/collapsed
   */
  toggleAllNodes() {
    if (this.areAllNodesExpanded()) {
      // Collapse all
      this.expandedNodes.clear();
    } else {
      // Expand all
      this.allExpandableNodeIds.forEach(id => this.expandedNodes.add(id));
    }
    this.redraw();
  }

  /**
   * Render the toggle all link
   */
  renderToggleAllLink() {
    if (this.allExpandableNodeIds.length === 0) return "";

    const isAllExpanded = this.areAllNodesExpanded();
    const linkText = isAllExpanded ? this.t("collapseAll") : this.t("expandAll");
    const ariaLabel = isAllExpanded
      ? this.t("collapseAllCategories")
      : this.t("expandAllCategories");

    return `
      <div class="ct-toggle-all-container">
        <a href="#" class="ct-toggle-all-link" ref="toggleAllLink" role="button" aria-label="${ariaLabel}">
          <i class="fa fa-${isAllExpanded ? "minus" : "plus"}-square-o" aria-hidden="true"></i>
          ${linkText}
        </a>
      </div>
    `;
  }

  render() {
    // Collect expandable node IDs before rendering
    this.allExpandableNodeIds = this.collectExpandableNodeIds(this.component.data?.tree || []);

    return super.render(`
      <div class="category-tree-wrapper" ref="wrapper" role="tree" aria-label="${this.t(this.component.label)}">
        ${this.renderToggleAllLink()}
        ${this.renderTree(this.component.data?.tree || [])}
      </div>
    `);
  }

  renderTree(nodes, depth = 0) {
    if (!nodes || nodes.length === 0) return "";
    return nodes.map(node => this.renderNode(node, depth)).join("");
  }

  renderNode(node, depth) {
    const hasChildren = node.children?.length > 0;
    const isExpanded = this.expandedNodes.has(String(node.id));
    const isSelected = this.selectedValues.has(String(node.id));
    const isDisabled = node.disabled || this.component.disabled;

    const inputType = this.component.multiple ? "checkbox" : "radio";
    const inputName = `ct-${this.component.key}-${this.id}`;

    // Description can contain HTML, so we use it as-is
    const description = node.description || "";
    const hasDescription = description && description.trim() !== "";

    const childrenId = `ct-children-${node.id}`;
    const statusClass = node.status ? `ct-node--${node.status}` : "";
    const dateBadge = this.renderDateBadge(node);

    return `
      <div class="ct-node ${statusClass}" data-depth="${depth}" data-id="${node.id}" role="treeitem" aria-expanded="${hasChildren ? isExpanded : undefined}" aria-selected="${isSelected}">
<div class="${isSelected ? "ct-selected" : ""}">
        <div class="ct-row">
          ${
            hasChildren
              ? `
            <button type="button" class="ct-toggle" data-id="${node.id}" aria-expanded="${isExpanded}" aria-controls="${childrenId}" aria-label="Toggle ${node.label}">
              <i class="fa fa-chevron-${isExpanded ? "down" : "right"}" aria-hidden="true"></i>
            </button>
          `
              : '<span class="ct-spacer" aria-hidden="true"></span>'
          }

          ${
            node.leaf
              ? `
            <label class="ct-label">
              <input type="${inputType}"
                     name="${inputName}"
                     value="${node.id}"
                     ${isSelected ? "checked" : ""}
                     ${isDisabled ? "disabled" : ""}
                     ref="input" />
              <span class="ct-label-text">${this.t(node.label)}</span>
              <span class="ct-code">(${node.code})</span>
              ${dateBadge}
            </label>
          `
              : `
            <div class="ct-parent-wrapper">
              <span class="ct-parent" data-id="${node.id}" tabindex="0" role="button" aria-expanded="${isExpanded}" aria-controls="${childrenId}">${this.t(node.label)}</span>
              <span class="ct-code">(${node.code})</span>
              ${dateBadge}
            </div>
          `
          }
        </div>
        ${
          hasDescription
            ? `<div class="ct-description" id="ct-desc-${node.id}">${description}</div>`
            : ""
        }
        </div>
        ${
          hasChildren
            ? `
          <div class="ct-children ${isExpanded ? "" : "collapsed"}" id="${childrenId}" role="group" aria-hidden="${!isExpanded}" data-parent-id="${node.id}">
            ${this.renderTree(node.children, depth + 1)}
          </div>
        `
            : ""
        }
      </div>
    `;
  }

  attach(element) {
    this.loadRefs(element, {
      wrapper: "single",
      input: "multiple",
      toggleAllLink: "single",
    });

    // Toggle all link handler
    if (this.refs.toggleAllLink) {
      this.addEventListener(this.refs.toggleAllLink, "click", e => {
        e.preventDefault();
        this.toggleAllNodes();
      });
    }

    // Toggle handlers for expand/collapse
    const toggleButtons = element.querySelectorAll(".ct-toggle");
    toggleButtons.forEach(btn => {
      this.addEventListener(btn, "click", e => {
        e.preventDefault();
        e.stopPropagation();
        const id = btn.dataset.id;
        this.toggleNode(id);
      });
    });

    // Parent label click and keyboard handlers for expand/collapse
    const parentLabels = element.querySelectorAll(".ct-parent");
    parentLabels.forEach(label => {
      this.addEventListener(label, "click", e => {
        e.preventDefault();
        const id = label.dataset.id;
        this.toggleNode(id);
      });

      // Keyboard accessibility: Enter and Space to toggle
      this.addEventListener(label, "keydown", e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          const id = label.dataset.id;
          this.toggleNode(id);
        }
      });
    });

    // Selection handlers for leaf nodes
    const inputs = element.querySelectorAll('input[type="checkbox"], input[type="radio"]');
    inputs.forEach(input => {
      this.addEventListener(input, "change", () => this.handleSelection(input));
    });

    return super.attach(element);
  }

  toggleNode(id) {
    const strId = String(id);
    if (this.expandedNodes.has(strId)) {
      this.expandedNodes.delete(strId);
    } else {
      this.expandedNodes.add(strId);
    }
    this.redraw();
  }

  handleSelection(input) {
    const { multiple, maxItemCount } = this.component;
    const id = String(input.value);

    if (multiple) {
      if (input.checked) {
        // Check maxItemCount constraint
        if (maxItemCount > 0 && this.selectedValues.size >= maxItemCount) {
          input.checked = false;
          return;
        }
        this.selectedValues.add(id);
      } else {
        this.selectedValues.delete(id);
      }
    } else {
      this.selectedValues.clear();
      if (input.checked) {
        this.selectedValues.add(id);
      }
    }

    this.updateValue();
    this.redraw();
  }

  getValue() {
    const values = Array.from(this.selectedValues);
    if (this.component.multiple) {
      return values.map(id => ({ value: id }));
    }
    return values.length > 0 ? { value: values[0] } : null;
  }

  setValue(value, flags = {}) {
    this.selectedValues.clear();

    if (value) {
      const values = Array.isArray(value) ? value : [value];
      values.forEach(v => {
        const id = typeof v === "object" && v !== null ? String(v.value) : String(v);
        if (id && id !== "null" && id !== "undefined") {
          this.selectedValues.add(id);
        }
      });
    }

    // Expand parents of selected nodes so they're visible
    this.expandParentsOfSelected();

    if (!flags.noUpdateEvent) {
      this.updateValue();
    }

    if (!flags.noRedraw) {
      this.redraw();
    }

    return super.setValue(value, flags);
  }

  expandParentsOfSelected() {
    const tree = this.component.data?.tree || [];

    const findAndExpandParents = (nodes, parents = []) => {
      nodes.forEach(node => {
        const nodeId = String(node.id);
        if (this.selectedValues.has(nodeId)) {
          // Expand all parent nodes
          parents.forEach(parentId => this.expandedNodes.add(parentId));
        }
        if (node.children && node.children.length > 0) {
          findAndExpandParents(node.children, [...parents, nodeId]);
        }
      });
    };

    findAndExpandParents(tree);
  }

  get emptyValue() {
    return this.component.multiple ? [] : null;
  }

  isEmpty(value = this.dataValue) {
    if (this.component.multiple) {
      return !value || !Array.isArray(value) || value.length === 0;
    }
    return value === null || value === undefined || value === "";
  }

  normalizeValue(value) {
    if (!value) {
      return this.emptyValue;
    }

    if (this.component.multiple) {
      if (!Array.isArray(value)) {
        return [{ value: String(value.value || value) }];
      }
      return value.map(v => ({
        value: String(typeof v === "object" && v !== null ? v.value : v),
      }));
    }

    if (typeof value === "object" && value !== null) {
      return { value: String(value.value) };
    }

    return { value: String(value) };
  }
}

// Register component
Formio.registerComponent("categoryTree", CustomCategoryTreeComponent);

export default CustomCategoryTreeComponent;
